import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Bot, Copy, Check, Save, RefreshCw, Sparkles, Webhook, ShieldCheck, Plus, Trash2, Upload, FileText, Brain, Plug, Server, Globe, Pencil, Zap, ChevronDown, ChevronUp, Play } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import PageHeader from '../../components/PageHeader';
import { PAGE, PageLoader } from '../../components/crm/ui';

const ALL_TOOLS = [
  { key: 'search_knowledge_base', label: 'Search knowledge base' },
  { key: 'create_support_ticket', label: 'Create support ticket' },
  { key: 'escalate_to_human', label: 'Escalate to human' },
  { key: 'get_ticket_status', label: 'Look up ticket status' },
  { key: 'lookup_contact_profile', label: 'Look up contact profile' },
  { key: 'get_invoice_status', label: 'Look up invoice status' },
  { key: 'get_conversation_history_summary', label: 'Summarize conversation history' },
  { key: 'get_current_time', label: 'Get current time' },
  { key: 'calculator', label: 'Calculator' },
  { key: 'get_order_status', label: 'Look up order status (requires API connector)' },
  { key: 'list_products', label: 'List products (requires API connector)' },
  // Universal CRM + messaging tools
  { key: 'create_lead', label: 'Create a CRM lead' },
  { key: 'search_leads', label: 'Search CRM leads' },
  { key: 'update_lead', label: 'Update a CRM lead' },
  { key: 'create_contact', label: 'Create a CRM contact' },
  { key: 'search_contacts', label: 'Search CRM contacts' },
  { key: 'create_deal', label: 'Create a CRM deal' },
  { key: 'create_task', label: 'Create a CRM task' },
  { key: 'send_whatsapp_message', label: 'Send a WhatsApp message' },
  { key: 'send_email', label: 'Send an email' },
  // Booking tools
  { key: 'get_available_slots', label: 'Get available appointment slots' },
  { key: 'book_appointment', label: 'Book an appointment' },
  { key: 'cancel_appointment', label: 'Cancel an appointment' },
  { key: 'reschedule_appointment', label: 'Reschedule an appointment' },
  // Selling tools
  { key: 'generate_payment_link', label: 'Generate a Razorpay payment link' },
  { key: 'create_quote', label: 'Create a sales quote / proposal' },
  { key: 'check_payment_status', label: 'Check payment status' },
  { key: 'list_catalog_products', label: 'List catalog products' },
];

const INDUSTRY_TEMPLATES = [
  {
    key: 'ecom',
    label: '🛍️ eCom / Retail',
    tools: ['search_knowledge_base', 'list_catalog_products', 'get_order_status', 'create_lead', 'generate_payment_link', 'check_payment_status', 'create_quote', 'send_whatsapp_message', 'escalate_to_human'],
    prompt: 'You are a helpful sales and support assistant for an online store. Help customers discover products, check order status, answer questions about shipping and returns, and process purchases. When a customer wants to buy, generate a payment link. Always be friendly and concise.',
  },
  {
    key: 'real_estate',
    label: '🏠 Real Estate',
    tools: ['search_knowledge_base', 'create_lead', 'get_available_slots', 'book_appointment', 'cancel_appointment', 'reschedule_appointment', 'send_whatsapp_message', 'create_deal', 'escalate_to_human'],
    prompt: 'You are a real estate assistant. Qualify buyer and tenant enquiries by asking about budget, location preference, property type (BHK), and timeline. Collect contact details and book property viewings. For serious buyers, create a CRM lead and deal. Always be professional and helpful.',
  },
  {
    key: 'healthcare',
    label: '🩺 Healthcare / Clinic',
    tools: ['search_knowledge_base', 'create_lead', 'get_available_slots', 'book_appointment', 'cancel_appointment', 'reschedule_appointment', 'send_whatsapp_message', 'escalate_to_human'],
    prompt: 'You are a patient-facing clinic assistant. Help patients book, reschedule, or cancel appointments. Answer questions about services, timings, and consultation fees. Do NOT provide medical advice or diagnoses — always direct clinical questions to the doctor. Be empathetic and clear.',
  },
  {
    key: 'legal',
    label: '⚖️ Legal / Law Firm',
    tools: ['search_knowledge_base', 'create_lead', 'get_available_slots', 'book_appointment', 'cancel_appointment', 'reschedule_appointment', 'send_whatsapp_message', 'create_deal', 'escalate_to_human'],
    prompt: 'You are a legal intake assistant. Understand the client\'s matter (civil, criminal, family, property, corporate), collect their name, contact, and a brief description of their issue, and book an initial consultation slot. Do NOT give legal advice. Always clarify that responses are informational only and a qualified lawyer will advise them during the consultation.',
  },
  {
    key: 'general',
    label: '🤖 General Support',
    tools: ['search_knowledge_base', 'create_support_ticket', 'escalate_to_human', 'get_ticket_status', 'create_lead', 'send_whatsapp_message', 'send_email'],
    prompt: 'You are a helpful customer support assistant. Answer questions using the knowledge base, create support tickets for issues that need follow-up, and escalate to a human when needed. Be polite, concise, and always confirm that you have understood the customer\'s issue.',
  },
];

const BLANK_REST_API_CONFIG = { url: '', method: 'GET', authType: 'none', authKeyName: '', authValue: '', responseDataPath: '', textField: '' };
const BLANK_MCP_CONFIG = { serverUrl: '', authType: 'none', authKeyName: '', authValue: '', resourceUris: '' };

const BLANK_API_TOOL = {
  name: '', description: '', method: 'GET', urlTemplate: '',
  authType: 'none', authKeyName: '', authValue: '',
  params: [], responseFields: [], isActive: true,
};

const BLANK_MCP_CONNECTOR = { name: '', serverUrl: '', authType: 'none', authKeyName: '', authValue: '' };

const DEFAULT_WIDGET = {
  name: 'Support Widget',
  industry: 'general',
  appearance: {
    primaryColor: '#0176D3', position: 'bottom-right', logoUrl: '', headerTitle: 'Chat with us',
    headerSubtitle: 'We typically reply in a few minutes', borderRadius: 16, darkMode: false,
  },
  behavior: { welcomeMessage: 'Hi! How can I help you today?', placeholderText: 'Type your message…', showBranding: true },
  customSystemPrompt: '',
  knowledgeBaseIds: [],
  enabledTools: ALL_TOOLS.map(t => t.key),
  allowedDomains: [],
  tokenLimits: { monthlyBudget: 500000, perConversationLimit: 20000, overBudgetMessage: "We've hit our monthly AI usage limit — I've logged this as a ticket and our team will follow up." },
  model: { name: 'gpt-4o-mini', temperature: 0.3, maxSteps: 6 },
  webhooks: [],
  security: { blockPromptInjection: true },
  channels: {
    whatsappBotEnabled: false, whatsappBotReplyDelay: 0, instagramBotEnabled: false, instagramBotReplyDelay: 0, facebookBotEnabled: false, facebookBotReplyDelay: 0, emailBotEnabled: false, emailBotReplyDelay: 0, telegramBotEnabled: false, telegramBotReplyDelay: 0, apiBotEnabled: false,
    whatsappIdentityVerification: { enabled: false, method: 'otp', lookupApiToolId: '', lookupPhoneParamName: '', identifierResponseField: '', otpTemplateName: '', otpTemplateLanguage: 'en_US', otpExpiryMinutes: 10, maxOtpAttempts: 5 },
  },
};

const WEBHOOK_EVENTS = [
  { key: 'ticket_created', label: 'Ticket created' },
  { key: 'escalated', label: 'Escalated to human' },
  { key: 'message_sent', label: 'Every agent reply' },
];

export default function SupportWidgetBuilderPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [widget, setWidget] = useState(null);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [domainInput, setDomainInput] = useState('');
  const [newKbName, setNewKbName] = useState('');
  const [newKbSourceType, setNewKbSourceType] = useState('upload');
  const [newKbRestConfig, setNewKbRestConfig] = useState(BLANK_REST_API_CONFIG);
  const [newKbMcpConfig, setNewKbMcpConfig] = useState(BLANK_MCP_CONFIG);
  const [newKbSyncInterval, setNewKbSyncInterval] = useState('0');
  const [creatingKb, setCreatingKb] = useState(false);
  const [uploadingKbId, setUploadingKbId] = useState(null);
  const [syncingKbId, setSyncingKbId] = useState(null);
  const [editingKbSourceId, setEditingKbSourceId] = useState(null);
  const [editKbConfig, setEditKbConfig] = useState(null);
  const [editKbSyncInterval, setEditKbSyncInterval] = useState('0');
  const fileInputRefs = useRef({});
  const [apiTools, setApiTools] = useState([]);
  const [editingApiTool, setEditingApiTool] = useState(null);
  const [savingApiTool, setSavingApiTool] = useState(false);
  const [testingApiToolId, setTestingApiToolId] = useState(null);
  const [apiToolTestArgs, setApiToolTestArgs] = useState({});
  const [apiToolTestResult, setApiToolTestResult] = useState(null);
  const [mcpConnectors, setMcpConnectors] = useState([]);
  const [editingMcpConnector, setEditingMcpConnector] = useState(null);
  const [savingMcpConnector, setSavingMcpConnector] = useState(false);
  const [discoveringMcpId, setDiscoveringMcpId] = useState(null);
  const [inboundEmail, setInboundEmail] = useState(null);

  const loadKnowledgeBases = useCallback(async () => {
    try {
      const res = await api.get('/support/knowledge-bases');
      setKnowledgeBases(res.data?.data || []);
    } catch {
      setKnowledgeBases([]);
    }
  }, []);

  const loadApiTools = useCallback(async () => {
    try {
      const res = await api.get('/support/api-tools');
      setApiTools(res.data?.data || []);
    } catch {
      setApiTools([]);
    }
  }, []);

  const loadMcpConnectors = useCallback(async () => {
    try {
      const res = await api.get('/support/mcp-connectors');
      setMcpConnectors(res.data?.data || []);
    } catch {
      setMcpConnectors([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [widgetsRes] = await Promise.all([
        api.get('/support/widget-config'),
        loadKnowledgeBases(),
        loadApiTools(),
        loadMcpConnectors(),
      ]);

      let existing = widgetsRes.data?.data?.[0];
      if (!existing) {
        const created = await api.post('/support/widget-config', { name: DEFAULT_WIDGET.name });
        existing = created.data.data;
      }
      setWidget({ ...DEFAULT_WIDGET, ...existing, appearance: { ...DEFAULT_WIDGET.appearance, ...existing.appearance }, behavior: { ...DEFAULT_WIDGET.behavior, ...existing.behavior }, tokenLimits: { ...DEFAULT_WIDGET.tokenLimits, ...existing.tokenLimits }, model: { ...DEFAULT_WIDGET.model, ...existing.model }, security: { ...DEFAULT_WIDGET.security, ...existing.security }, webhooks: existing.webhooks || [] });
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to load widget configuration.');
    } finally {
      setLoading(false);
    }
  }, [loadKnowledgeBases, loadApiTools, loadMcpConnectors]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/email/inbound-config')
      .then(res => setInboundEmail(res.data?.data?.address || null))
      .catch(() => setInboundEmail(null));
  }, []);

  const patch = (path, value) => {
    setWidget(prev => {
      const next = { ...prev };
      let obj = next;
      for (let i = 0; i < path.length - 1; i++) {
        obj[path[i]] = { ...obj[path[i]] };
        obj = obj[path[i]];
      }
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const toggleTool = (key) => {
    setWidget(prev => {
      const has = prev.enabledTools.includes(key);
      return { ...prev, enabledTools: has ? prev.enabledTools.filter(t => t !== key) : [...prev.enabledTools, key] };
    });
  };

  const toggleKb = (id) => {
    setWidget(prev => {
      const has = prev.knowledgeBaseIds.includes(id);
      return { ...prev, knowledgeBaseIds: has ? prev.knowledgeBaseIds.filter(k => k !== id) : [...prev.knowledgeBaseIds, id] };
    });
  };

  const createKnowledgeBase = async () => {
    const name = newKbName.trim();
    if (!name) return;
    if (newKbSourceType === 'rest_api' && !newKbRestConfig.url.trim()) {
      return alertService.error('Missing Info', 'A source URL is required for a REST API knowledge base.');
    }
    if (newKbSourceType === 'mcp_server' && !newKbMcpConfig.serverUrl.trim()) {
      return alertService.error('Missing Info', 'A server URL is required for an MCP server knowledge base.');
    }
    setCreatingKb(true);
    try {
      const payload = { name, vectorBackend: 'pgvector', sourceType: newKbSourceType, syncIntervalMinutes: newKbSyncInterval };
      if (newKbSourceType === 'rest_api') payload.restApiConfig = newKbRestConfig;
      if (newKbSourceType === 'mcp_server') {
        payload.mcpServerConfig = {
          ...newKbMcpConfig,
          resourceUris: newKbMcpConfig.resourceUris.split(',').map(s => s.trim()).filter(Boolean),
        };
      }
      const res = await api.post('/support/knowledge-bases', payload);
      const kb = res.data.data;
      setKnowledgeBases(prev => [kb, ...prev]);
      setWidget(prev => ({ ...prev, knowledgeBaseIds: [...prev.knowledgeBaseIds, kb._id] }));
      setNewKbName('');
      setNewKbSourceType('upload');
      setNewKbRestConfig(BLANK_REST_API_CONFIG);
      setNewKbMcpConfig(BLANK_MCP_CONFIG);
      setNewKbSyncInterval('0');
      const nextStep = newKbSourceType === 'upload'
        ? 'upload documents to it below'
        : 'it is now fetching and indexing data in the background — watch the status badge below';
      alertService.success('Created', `"${name}" knowledge base created — ${nextStep}, then Save Widget to attach it.`);
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to create knowledge base.');
    } finally {
      setCreatingKb(false);
    }
  };

  const syncKnowledgeBase = async (kbId) => {
    setSyncingKbId(kbId);
    try {
      await api.post(`/support/knowledge-bases/${kbId}/sync`);
      alertService.success('Syncing', 'Re-fetching and re-indexing this source — status will update to "ready" shortly.');
      await loadKnowledgeBases();
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to start sync.');
    } finally {
      setSyncingKbId(null);
    }
  };

  const startEditKbSource = (kb) => {
    setEditingKbSourceId(kb._id);
    setEditKbSyncInterval(String(kb.syncIntervalMinutes || 0));
    if (kb.sourceType === 'rest_api') {
      setEditKbConfig({ ...BLANK_REST_API_CONFIG, ...kb.restApiConfig, authValue: '' });
    } else if (kb.sourceType === 'mcp_server') {
      setEditKbConfig({ ...BLANK_MCP_CONFIG, ...kb.mcpServerConfig, resourceUris: (kb.mcpServerConfig.resourceUris || []).join(', '), authValue: '' });
    }
  };

  const saveKbSourceConfig = async (kb) => {
    try {
      const payload = { syncIntervalMinutes: editKbSyncInterval };
      if (kb.sourceType === 'rest_api') payload.restApiConfig = editKbConfig;
      if (kb.sourceType === 'mcp_server') {
        payload.mcpServerConfig = {
          ...editKbConfig,
          resourceUris: editKbConfig.resourceUris.split(',').map(s => s.trim()).filter(Boolean),
        };
      }
      const res = await api.put(`/support/knowledge-bases/${kb._id}`, payload);
      setKnowledgeBases(prev => prev.map(k => k._id === kb._id ? res.data.data : k));
      setEditingKbSourceId(null);
      alertService.success('Saved', 'Source config updated — click "Sync now" to re-fetch with the new settings.');
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to update source config.');
    }
  };

  const uploadKbFiles = async (kbId, files) => {
    if (!files?.length) return;
    setUploadingKbId(kbId);
    const form = new FormData();
    Array.from(files).forEach(f => form.append('files', f));
    try {
      await api.post(`/support/knowledge-bases/${kbId}/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      alertService.success('Uploading', 'Files are being processed — status will update to "ready" once indexed (usually under a minute).');
      await loadKnowledgeBases();
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to upload files.');
    } finally {
      setUploadingKbId(null);
    }
  };

  const deleteKnowledgeBase = async (kbId) => {
    try {
      await api.delete(`/support/knowledge-bases/${kbId}`);
      setKnowledgeBases(prev => prev.filter(k => k._id !== kbId));
      setWidget(prev => ({ ...prev, knowledgeBaseIds: prev.knowledgeBaseIds.filter(id => id !== kbId) }));
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to delete knowledge base.');
    }
  };

  const saveApiTool = async () => {
    if (!editingApiTool.name?.trim() || !editingApiTool.urlTemplate?.trim() || !editingApiTool.description?.trim()) {
      return alertService.error('Missing Info', 'Name, description, and URL are required.');
    }
    setSavingApiTool(true);
    try {
      if (editingApiTool._id) {
        const res = await api.put(`/support/api-tools/${editingApiTool._id}`, editingApiTool);
        setApiTools(prev => prev.map(t => t._id === editingApiTool._id ? res.data.data : t));
      } else {
        const res = await api.post('/support/api-tools', editingApiTool);
        setApiTools(prev => [res.data.data, ...prev]);
      }
      setEditingApiTool(null);
      setApiToolTestResult(null);
      alertService.success('Saved', 'API tool saved — the agent can now call it.');
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to save API tool.');
    } finally {
      setSavingApiTool(false);
    }
  };

  const deleteApiTool = async (id) => {
    try {
      await api.delete(`/support/api-tools/${id}`);
      setApiTools(prev => prev.filter(t => t._id !== id));
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to delete API tool.');
    }
  };

  const testApiTool = async (tool) => {
    setTestingApiToolId(tool._id);
    setApiToolTestResult(null);
    try {
      const res = await api.post(`/support/api-tools/${tool._id}/test`, { args: apiToolTestArgs });
      setApiToolTestResult(res.data.data);
    } catch (err) {
      setApiToolTestResult({ error: err.response?.data?.error || 'Request failed' });
    } finally {
      setTestingApiToolId(null);
    }
  };

  const saveMcpConnector = async () => {
    if (!editingMcpConnector.name?.trim() || !editingMcpConnector.serverUrl?.trim()) {
      return alertService.error('Missing Info', 'Name and server URL are required.');
    }
    setSavingMcpConnector(true);
    try {
      if (editingMcpConnector._id) {
        const res = await api.put(`/support/mcp-connectors/${editingMcpConnector._id}`, editingMcpConnector);
        setMcpConnectors(prev => prev.map(c => c._id === editingMcpConnector._id ? res.data.data : c));
      } else {
        const res = await api.post('/support/mcp-connectors', editingMcpConnector);
        setMcpConnectors(prev => [res.data.data, ...prev]);
        alertService.success('Created', 'Connecting and discovering this server\'s tools in the background — refresh in a moment to enable them.');
      }
      setEditingMcpConnector(null);
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to save MCP connector.');
    } finally {
      setSavingMcpConnector(false);
    }
  };

  const deleteMcpConnector = async (id) => {
    try {
      await api.delete(`/support/mcp-connectors/${id}`);
      setMcpConnectors(prev => prev.filter(c => c._id !== id));
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to delete MCP connector.');
    }
  };

  const discoverMcpTools = async (id) => {
    setDiscoveringMcpId(id);
    try {
      await api.post(`/support/mcp-connectors/${id}/discover`);
      alertService.success('Discovering', 'Connecting to the server and listing its tools — status will update to "ready" shortly.');
      await loadMcpConnectors();
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to start discovery.');
    } finally {
      setDiscoveringMcpId(null);
    }
  };

  const toggleMcpConnectorTool = async (connector, toolName) => {
    const nextTools = connector.tools.map(t => t.name === toolName ? { ...t, enabled: !t.enabled } : t);
    setMcpConnectors(prev => prev.map(c => c._id === connector._id ? { ...c, tools: nextTools } : c));
    try {
      await api.put(`/support/mcp-connectors/${connector._id}`, {
        tools: nextTools.map(t => ({ name: t.name, enabled: t.enabled })),
      });
    } catch (err) {
      setMcpConnectors(prev => prev.map(c => c._id === connector._id ? connector : c)); // revert on failure
      alertService.error('Error', err.response?.data?.error || 'Failed to update tool.');
    }
  };

  const addDomain = () => {
    const domain = domainInput.trim();
    if (!domain) return;
    setWidget(prev => ({ ...prev, allowedDomains: [...(prev.allowedDomains || []), domain] }));
    setDomainInput('');
  };

  const removeDomain = (domain) => {
    setWidget(prev => ({ ...prev, allowedDomains: prev.allowedDomains.filter(d => d !== domain) }));
  };

  const addWebhook = () => {
    setWidget(prev => ({
      ...prev,
      webhooks: [...(prev.webhooks || []), { url: '', events: ['ticket_created', 'escalated'], secret: '', isActive: true }],
    }));
  };

  const patchWebhook = (idx, field, value) => {
    setWidget(prev => ({
      ...prev,
      webhooks: prev.webhooks.map((w, i) => i === idx ? { ...w, [field]: value } : w),
    }));
  };

  const toggleWebhookEvent = (idx, eventKey) => {
    setWidget(prev => ({
      ...prev,
      webhooks: prev.webhooks.map((w, i) => {
        if (i !== idx) return w;
        const has = w.events.includes(eventKey);
        return { ...w, events: has ? w.events.filter(e => e !== eventKey) : [...w.events, eventKey] };
      }),
    }));
  };

  const removeWebhook = (idx) => {
    setWidget(prev => ({ ...prev, webhooks: prev.webhooks.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/support/widget-config/${widget._id}`, widget);
      setWidget(prev => ({ ...prev, ...res.data.data }));
      alertService.success('Saved', 'Widget configuration saved.');
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !widget) {
    return <PageLoader />;
  }

  const embedCode = `<script src="${window.location.origin}/widget/loader.js" data-widget-key="${widget.publicWidgetKey}" data-position="${widget.appearance.position}"></script>`;
  const previewUrl = `${window.location.origin}/widget/chat.html?key=${widget.publicWidgetKey}`;

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    alertService.success('Copied', 'Embed script copied — paste it before </body> on any website.');
  };

  return (
    <div className={PAGE}>
      <PageHeader
        title="Support Widget"
        subtitle="Configure the AI support chat widget you embed on your website."
        icon={Bot}
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-7 space-y-6">
          <div className="sf-card p-6 space-y-3">
            <h2 className="sf-card-title flex items-center gap-1.5">🏭 Industry</h2>
            <p className="text-xs text-[#706E6B]">
              Sets the token cost multiplier for this widget and filters usage analytics by industry. Affects billing credits consumed per conversation.
            </p>
            <select
              value={widget.industry || 'general'}
              onChange={e => patch(['industry'], e.target.value)}
              className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]"
            >
              <option value="general">🤖 General / SaaS (1× base rate)</option>
              <option value="ecom">🛍️ eCom / Retail (1× base rate)</option>
              <option value="real_estate">🏠 Real Estate (1.5× premium)</option>
              <option value="healthcare">🩺 Healthcare / Clinic (1.5× premium)</option>
              <option value="legal">⚖️ Legal / Law Firm (2× premium)</option>
            </select>
          </div>

          <div className="sf-card p-6 space-y-4">
            <h2 className="sf-card-title">Appearance</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Primary color</label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={widget.appearance.primaryColor} onChange={e => patch(['appearance', 'primaryColor'], e.target.value)} className="w-10 h-10 rounded border border-[#D8DDE6] cursor-pointer p-0" />
                  <input type="text" value={widget.appearance.primaryColor} onChange={e => patch(['appearance', 'primaryColor'], e.target.value)} className="flex-1 px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono" />
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Position</label>
                <select value={widget.appearance.position} onChange={e => patch(['appearance', 'position'], e.target.value)} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                  <option value="bottom-right">Bottom right</option>
                  <option value="bottom-left">Bottom left</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Logo URL</label>
                <input type="text" value={widget.appearance.logoUrl || ''} onChange={e => patch(['appearance', 'logoUrl'], e.target.value)} placeholder="https://yoursite.com/logo.png" className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Header title</label>
                <input type="text" value={widget.appearance.headerTitle} onChange={e => patch(['appearance', 'headerTitle'], e.target.value)} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Header subtitle</label>
                <input type="text" value={widget.appearance.headerSubtitle} onChange={e => patch(['appearance', 'headerSubtitle'], e.target.value)} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
              </div>
              <label className="col-span-2 flex items-center gap-2 text-sm font-medium text-[#080707]">
                <input type="checkbox" checked={widget.appearance.darkMode} onChange={e => patch(['appearance', 'darkMode'], e.target.checked)} />
                Dark mode
              </label>
            </div>
          </div>

          <div className="sf-card p-6 space-y-4">
            <h2 className="sf-card-title">Behavior</h2>
            <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Welcome message</label>
              <input type="text" value={widget.behavior.welcomeMessage} onChange={e => patch(['behavior', 'welcomeMessage'], e.target.value)} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Input placeholder</label>
              <input type="text" value={widget.behavior.placeholderText} onChange={e => patch(['behavior', 'placeholderText'], e.target.value)} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-[#080707]">
              <input type="checkbox" checked={widget.behavior.showBranding} onChange={e => patch(['behavior', 'showBranding'], e.target.checked)} />
              Show "Powered by" branding
            </label>
          </div>

          <div className="sf-card p-6 space-y-3">
            <h2 className="sf-card-title flex items-center gap-1.5"><Sparkles size={16} className="text-[#DD7A01]" /> Custom instructions</h2>
            <p className="text-xs text-[#706E6B]">
              Appended to our fixed base system prompt (which always enforces no-fabrication and escalation
              rules) — use this for your product details, tone, and policies, not to change core behavior.
            </p>
            <div>
              <p className="text-xs font-semibold text-[#3E3E3C] mb-2">Industry preset</p>
              <div className="flex flex-wrap gap-2">
                {INDUSTRY_TEMPLATES.map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => {
                      patch(['customSystemPrompt'], t.prompt);
                      setWidget(prev => ({ ...prev, enabledTools: t.tools }));
                    }}
                    className="px-3 py-1.5 text-[13px] border border-[#D8DDE6] rounded hover:border-[#0176D3] hover:text-[#0176D3] transition-colors bg-white"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[#706E6B] mt-1.5">Clicking a preset fills the prompt below and selects the recommended tools — you can edit either afterward.</p>
            </div>
            <textarea
              value={widget.customSystemPrompt}
              onChange={e => patch(['customSystemPrompt'], e.target.value)}
              placeholder="e.g. We are Acme Corp, a project management SaaS. Refunds are available within 14 days…"
              className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] h-28"
            />
          </div>

          <div className="sf-card p-6 space-y-4">
            <h2 className="sf-card-title flex items-center gap-1.5"><Brain size={16} className="text-[#0176D3]" /> Knowledge bases</h2>
            <p className="text-xs text-[#706E6B]">
              Feed the agent's <code className="font-mono">search_knowledge_base</code> tool from three kinds
              of source — uploaded documents, a REST API you point it at, or an MCP server it connects to as a
              client. Whichever you pick, the content gets chunked, embedded, and searched the same way.
            </p>

            <div className="space-y-3 border border-[#D8DDE6] rounded-lg p-3 bg-[#F7F9FB]">
              <input
                type="text"
                value={newKbName}
                onChange={e => setNewKbName(e.target.value)}
                placeholder="e.g. Product Docs"
                className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
              />

              <div className="flex gap-1.5">
                {[
                  { key: 'upload', label: 'Upload Docs', icon: Upload },
                  { key: 'rest_api', label: 'REST API', icon: Globe },
                  { key: 'mcp_server', label: 'MCP Server', icon: Server },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setNewKbSourceType(opt.key)}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded text-[13px] font-semibold border ${newKbSourceType === opt.key ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white text-[#080707] border-[#D8DDE6]'}`}
                  >
                    <opt.icon size={13} /> {opt.label}
                  </button>
                ))}
              </div>

              {newKbSourceType === 'rest_api' && (
                <div className="space-y-2 pt-1">
                  <div className="flex gap-2">
                    <select
                      value={newKbRestConfig.method}
                      onChange={e => setNewKbRestConfig(prev => ({ ...prev, method: e.target.value }))}
                      className="px-2 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                    </select>
                    <input
                      type="text"
                      value={newKbRestConfig.url}
                      onChange={e => setNewKbRestConfig(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://api.example.com/faqs"
                      className="flex-1 px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={newKbRestConfig.responseDataPath}
                      onChange={e => setNewKbRestConfig(prev => ({ ...prev, responseDataPath: e.target.value }))}
                      placeholder="Response array path e.g. data.articles"
                      className="px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                    />
                    <input
                      type="text"
                      value={newKbRestConfig.textField}
                      onChange={e => setNewKbRestConfig(prev => ({ ...prev, textField: e.target.value }))}
                      placeholder="Text field per item e.g. body"
                      className="px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select
                      value={newKbRestConfig.authType}
                      onChange={e => setNewKbRestConfig(prev => ({ ...prev, authType: e.target.value }))}
                      className="px-2 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                    >
                      <option value="none">No auth</option>
                      <option value="bearer">Bearer token</option>
                      <option value="header">Custom header</option>
                      <option value="query">Query param</option>
                    </select>
                    {newKbRestConfig.authType !== 'none' && newKbRestConfig.authType !== 'bearer' && (
                      <input
                        type="text"
                        value={newKbRestConfig.authKeyName}
                        onChange={e => setNewKbRestConfig(prev => ({ ...prev, authKeyName: e.target.value }))}
                        placeholder="Header/param name"
                        className="px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                      />
                    )}
                    {newKbRestConfig.authType !== 'none' && (
                      <input
                        type="password"
                        value={newKbRestConfig.authValue}
                        onChange={e => setNewKbRestConfig(prev => ({ ...prev, authValue: e.target.value }))}
                        placeholder="Secret value"
                        className="px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                      />
                    )}
                  </div>
                </div>
              )}

              {newKbSourceType === 'mcp_server' && (
                <div className="space-y-2 pt-1">
                  <input
                    type="text"
                    value={newKbMcpConfig.serverUrl}
                    onChange={e => setNewKbMcpConfig(prev => ({ ...prev, serverUrl: e.target.value }))}
                    placeholder="https://mcp.example.com (Streamable HTTP endpoint)"
                    className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                  />
                  <input
                    type="text"
                    value={newKbMcpConfig.resourceUris}
                    onChange={e => setNewKbMcpConfig(prev => ({ ...prev, resourceUris: e.target.value }))}
                    placeholder="Resource URIs to pull, comma-separated (blank = all)"
                    className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select
                      value={newKbMcpConfig.authType}
                      onChange={e => setNewKbMcpConfig(prev => ({ ...prev, authType: e.target.value }))}
                      className="px-2 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                    >
                      <option value="none">No auth</option>
                      <option value="bearer">Bearer token</option>
                      <option value="header">Custom header</option>
                    </select>
                    {newKbMcpConfig.authType === 'header' && (
                      <input
                        type="text"
                        value={newKbMcpConfig.authKeyName}
                        onChange={e => setNewKbMcpConfig(prev => ({ ...prev, authKeyName: e.target.value }))}
                        placeholder="Header name"
                        className="px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                      />
                    )}
                    {newKbMcpConfig.authType !== 'none' && (
                      <input
                        type="password"
                        value={newKbMcpConfig.authValue}
                        onChange={e => setNewKbMcpConfig(prev => ({ ...prev, authValue: e.target.value }))}
                        placeholder="Secret value"
                        className="px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                      />
                    )}
                  </div>
                </div>
              )}

              {newKbSourceType !== 'upload' && (
                <div className="flex items-center gap-2">
                  <label className="text-[12px] font-semibold text-[#706E6B] whitespace-nowrap">Auto-sync every</label>
                  <input
                    type="number"
                    min="0"
                    value={newKbSyncInterval}
                    onChange={e => setNewKbSyncInterval(e.target.value)}
                    className="w-20 px-2 py-1.5 bg-white border border-[#E5E5E5] rounded text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                  />
                  <span className="text-xs text-[#706E6B]">minutes (0 = manual sync only)</span>
                </div>
              )}

              <button
                type="button"
                onClick={createKnowledgeBase}
                disabled={creatingKb || !newKbName.trim()}
                className="hover:bg-[#014486] w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-[#0176D3] text-white rounded whitespace-nowrap text-[13px] font-semibold disabled:opacity-60"
              >
                {creatingKb ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />} Create Knowledge Base
              </button>
            </div>

            {knowledgeBases.length === 0 ? (
              <p className="text-xs text-[#706E6B] italic">No knowledge bases yet — create one above.</p>
            ) : (
              <div className="space-y-3">
                {knowledgeBases.map(kb => (
                  <div key={kb._id} className="border border-[#D8DDE6] rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm font-medium text-[#080707]">
                        <input type="checkbox" checked={widget.knowledgeBaseIds.includes(kb._id)} onChange={() => toggleKb(kb._id)} />
                        {kb.name}
                        <span className="flex items-center gap-1 text-[10px] font-bold text-[#706E6B] uppercase">
                          {kb.sourceType === 'rest_api' ? <Globe size={11} /> : kb.sourceType === 'mcp_server' ? <Server size={11} /> : <Upload size={11} />}
                          {(kb.sourceType || 'upload').replace('_', ' ')}
                        </span>
                      </label>
                      <div className="flex items-center gap-1">
                        {kb.sourceType === 'upload' && (
                          <>
                            <input
                              type="file"
                              multiple
                              accept=".pdf,.docx,.txt,.md"
                              ref={el => (fileInputRefs.current[kb._id] = el)}
                              onChange={e => uploadKbFiles(kb._id, e.target.files)}
                              className="hidden"
                            />
                            <button
                              type="button"
                              onClick={() => fileInputRefs.current[kb._id]?.click()}
                              disabled={uploadingKbId === kb._id}
                              className="flex items-center gap-1 px-2 py-1 bg-[#EAF4FE] text-[#0176D3] rounded text-[13px] font-semibold disabled:opacity-60"
                            >
                              {uploadingKbId === kb._id ? <RefreshCw size={12} className="animate-spin" /> : <Upload size={12} />} Upload
                            </button>
                          </>
                        )}
                        {kb.sourceType !== 'upload' && (
                          <>
                            <button
                              type="button"
                              onClick={() => startEditKbSource(kb)}
                              className="flex items-center gap-1 px-2 py-1 bg-[#F3F2F2] text-[#080707] rounded text-[13px] font-semibold"
                            >
                              <Pencil size={12} /> Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => syncKnowledgeBase(kb._id)}
                              disabled={syncingKbId === kb._id || kb.syncStatus === 'syncing'}
                              className="flex items-center gap-1 px-2 py-1 bg-[#EAF4FE] text-[#0176D3] rounded text-[13px] font-semibold disabled:opacity-60"
                            >
                              {(syncingKbId === kb._id || kb.syncStatus === 'syncing') ? <RefreshCw size={12} className="animate-spin" /> : <RefreshCw size={12} />} Sync now
                            </button>
                          </>
                        )}
                        <button type="button" onClick={() => deleteKnowledgeBase(kb._id)} className="p-1.5 text-[#BA0517] hover:bg-[#FDE7E9] rounded"><Trash2 size={14} /></button>
                      </div>
                    </div>

                    {kb.sourceType !== 'upload' && (
                      <div className="flex items-center gap-2 pl-1 text-[11px] text-[#706E6B]">
                        <span className="flex-1 truncate font-mono">
                          {kb.sourceType === 'rest_api' ? kb.restApiConfig?.url : kb.mcpServerConfig?.serverUrl}
                        </span>
                        <span>{kb.syncIntervalMinutes > 0 ? `auto every ${kb.syncIntervalMinutes}m` : 'manual sync'}</span>
                        <span className={`sf-badge ${kb.syncStatus === 'ready' ? 'sf-badge-success' : kb.syncStatus === 'failed' ? 'sf-badge-error' : 'sf-badge-warning'}`}>
                          {kb.syncStatus || 'idle'}{kb.syncStatus === 'ready' ? ` · ${kb.chunkCount} chunks` : ''}
                        </span>
                      </div>
                    )}
                    {kb.syncStatus === 'failed' && kb.syncError && (
                      <p className="text-[11px] text-[#BA0517] pl-1">{kb.syncError}</p>
                    )}

                    {editingKbSourceId === kb._id && editKbConfig && (
                      <div className="space-y-2 pt-2 border-t border-[#D8DDE6]">
                        {kb.sourceType === 'rest_api' && (
                          <>
                            <input
                              type="text"
                              value={editKbConfig.url}
                              onChange={e => setEditKbConfig(prev => ({ ...prev, url: e.target.value }))}
                              placeholder="Source URL"
                              className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                            />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={editKbConfig.responseDataPath}
                                onChange={e => setEditKbConfig(prev => ({ ...prev, responseDataPath: e.target.value }))}
                                placeholder="Response array path"
                                className="px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                              />
                              <input
                                type="text"
                                value={editKbConfig.textField}
                                onChange={e => setEditKbConfig(prev => ({ ...prev, textField: e.target.value }))}
                                placeholder="Text field per item"
                                className="px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                              />
                            </div>
                          </>
                        )}
                        {kb.sourceType === 'mcp_server' && (
                          <>
                            <input
                              type="text"
                              value={editKbConfig.serverUrl}
                              onChange={e => setEditKbConfig(prev => ({ ...prev, serverUrl: e.target.value }))}
                              placeholder="MCP server URL"
                              className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                            />
                            <input
                              type="text"
                              value={editKbConfig.resourceUris}
                              onChange={e => setEditKbConfig(prev => ({ ...prev, resourceUris: e.target.value }))}
                              placeholder="Resource URIs, comma-separated (blank = all)"
                              className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                            />
                          </>
                        )}
                        <input
                          type="password"
                          value={editKbConfig.authValue}
                          onChange={e => setEditKbConfig(prev => ({ ...prev, authValue: e.target.value }))}
                          placeholder={kb.restApiConfig?.hasAuthValue || kb.mcpServerConfig?.hasAuthValue ? 'Leave blank to keep existing secret' : 'Secret value (if auth required)'}
                          className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                        />
                        <div className="flex items-center gap-2">
                          <label className="text-[12px] font-semibold text-[#706E6B] whitespace-nowrap">Auto-sync every</label>
                          <input
                            type="number"
                            min="0"
                            value={editKbSyncInterval}
                            onChange={e => setEditKbSyncInterval(e.target.value)}
                            className="w-20 px-2 py-1.5 bg-white border border-[#E5E5E5] rounded text-[13px] shadow-[0_1px_3px_rgba(0,0,0,0.08)]"
                          />
                          <span className="text-xs text-[#706E6B]">minutes (0 = manual sync only)</span>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => saveKbSourceConfig(kb)} className="px-3 py-1.5 bg-[#0176D3] text-white rounded text-[13px] font-semibold">Save</button>
                          <button type="button" onClick={() => setEditingKbSourceId(null)} className="px-3 py-1.5 bg-[#F3F2F2] text-[#080707] rounded text-[13px] font-semibold">Cancel</button>
                        </div>
                      </div>
                    )}

                    {kb.files?.length > 0 && (
                      <div className="space-y-1 pl-1">
                        {kb.files.map((f, i) => (
                          <div key={i} className="flex items-center gap-2 text-[11px] text-[#706E6B]">
                            <FileText size={12} />
                            <span className="flex-1 truncate">{f.originalName}</span>
                            <span className={`sf-badge ${f.status === 'ready' ? 'sf-badge-success' : f.status === 'failed' ? 'sf-badge-error' : 'sf-badge-warning'}`}>
                              {f.status}{f.status === 'ready' ? ` · ${f.chunkCount} chunks` : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="sf-card p-6 space-y-4">
            <h2 className="sf-card-title flex items-center gap-1.5"><Zap size={16} className="text-[#0176D3]" /> REST API Tools</h2>
            <p className="text-xs text-[#706E6B]">
              Define any number of REST API endpoints as callable tools. Each tool you add becomes a
              function the agent can invoke — it decides when to call each one based on its name and
              description. Use <code className="font-mono">{'{param}'}</code> in the URL for path parameters.
            </p>

            {apiTools.length === 0 && !editingApiTool && (
              <p className="text-xs text-[#706E6B] italic">No API tools defined yet.</p>
            )}

            <div className="space-y-2">
              {apiTools.map(t => (
                <div key={t._id} className="flex items-center justify-between border border-[#D8DDE6] rounded-lg p-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#080707] font-mono">{t.name}</span>
                      <span className={`sf-badge ${t.isActive ? 'sf-badge-success' : 'sf-badge-neutral'}`}>{t.isActive ? 'active' : 'inactive'}</span>
                      <span className="sf-badge sf-badge-neutral">{t.method}</span>
                    </div>
                    <p className="text-[11px] text-[#706E6B] truncate mt-0.5">{t.description}</p>
                    <p className="text-[11px] text-[#706E6B] font-mono truncate">{t.urlTemplate}</p>
                  </div>
                  <div className="flex items-center gap-1 ml-3">
                    <button type="button" onClick={() => { setEditingApiTool({ ...BLANK_API_TOOL, ...t, params: t.params || [], responseFields: t.responseFields || [], authValue: '' }); setApiToolTestResult(null); setApiToolTestArgs({}); }} className="px-2 py-1 text-[13px] font-semibold text-[#0176D3] hover:bg-[#EAF4FE] rounded">Edit</button>
                    <button type="button" onClick={() => deleteApiTool(t._id)} className="p-1.5 text-[#BA0517] hover:bg-[#FDE7E9] rounded"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>

            {editingApiTool ? (
              <div className="border border-[#D8DDE6] rounded-lg p-4 space-y-4 bg-[#F9FAFB]">
                {/* Basic info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Tool name <span className="normal-case text-[#706E6B]">(snake_case, what the AI calls)</span></label>
                    <input type="text" value={editingApiTool.name} onChange={e => setEditingApiTool(p => ({ ...p, name: e.target.value }))} placeholder="e.g. get_order_status" className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Method</label>
                    <select value={editingApiTool.method} onChange={e => setEditingApiTool(p => ({ ...p, method: e.target.value }))} className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px]">
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Description <span className="normal-case">(tells the AI when to use this tool)</span></label>
                  <input type="text" value={editingApiTool.description} onChange={e => setEditingApiTool(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Look up a customer's order status by order number" className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px]" />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">URL template <span className="normal-case">(use {'{param}'} for path params)</span></label>
                  <input type="text" value={editingApiTool.urlTemplate} onChange={e => setEditingApiTool(p => ({ ...p, urlTemplate: e.target.value }))} placeholder="https://api.example.com/orders/{orderId}" className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono" />
                </div>

                {/* Auth */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1 border-t border-[#D8DDE6]">
                  <div>
                    <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Auth</label>
                    <select value={editingApiTool.authType} onChange={e => setEditingApiTool(p => ({ ...p, authType: e.target.value }))} className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px]">
                      <option value="none">None</option>
                      <option value="bearer">Bearer token</option>
                      <option value="header">Custom header</option>
                      <option value="query">Query param</option>
                    </select>
                  </div>
                  {editingApiTool.authType !== 'none' && editingApiTool.authType !== 'bearer' && (
                    <div>
                      <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">{editingApiTool.authType === 'header' ? 'Header name' : 'Param name'}</label>
                      <input type="text" value={editingApiTool.authKeyName} onChange={e => setEditingApiTool(p => ({ ...p, authKeyName: e.target.value }))} placeholder={editingApiTool.authType === 'header' ? 'X-API-Key' : 'api_key'} className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono" />
                    </div>
                  )}
                  {editingApiTool.authType !== 'none' && (
                    <div className={editingApiTool.authType === 'bearer' ? 'col-span-2' : ''}>
                      <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">{editingApiTool._id ? 'New secret (blank = keep existing)' : 'Secret'}</label>
                      <input type="password" value={editingApiTool.authValue} onChange={e => setEditingApiTool(p => ({ ...p, authValue: e.target.value }))} className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono" />
                    </div>
                  )}
                </div>

                {/* Parameters */}
                <div className="pt-1 border-t border-[#D8DDE6] space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-bold text-[#706E6B] uppercase ">Parameters</p>
                    <button type="button" onClick={() => setEditingApiTool(p => ({ ...p, params: [...p.params, { name: '', type: 'string', in: 'path', required: true, description: '' }] }))} className="flex items-center gap-1 px-2 py-1 bg-[#EAF4FE] text-[#0176D3] rounded text-[13px] font-semibold">
                      <Plus size={11} /> Add param
                    </button>
                  </div>
                  {editingApiTool.params.length === 0 && <p className="text-[11px] text-[#706E6B] italic">No parameters — the tool takes no inputs.</p>}
                  {editingApiTool.params.map((param, idx) => (
                    <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-1.5 items-center">
                      <input type="text" value={param.name} onChange={e => setEditingApiTool(p => ({ ...p, params: p.params.map((x, i) => i === idx ? { ...x, name: e.target.value } : x) }))} placeholder="name" className="col-span-2 px-2 py-1.5 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono" />
                      <select value={param.type} onChange={e => setEditingApiTool(p => ({ ...p, params: p.params.map((x, i) => i === idx ? { ...x, type: e.target.value } : x) }))} className="col-span-2 px-2 py-1.5 bg-white border border-[#E5E5E5] rounded text-[13px]">
                        <option value="string">string</option>
                        <option value="number">number</option>
                        <option value="boolean">boolean</option>
                      </select>
                      <select value={param.in} onChange={e => setEditingApiTool(p => ({ ...p, params: p.params.map((x, i) => i === idx ? { ...x, in: e.target.value } : x) }))} className="col-span-2 px-2 py-1.5 bg-white border border-[#E5E5E5] rounded text-[13px]">
                        <option value="path">path</option>
                        <option value="query">query</option>
                        <option value="body">body</option>
                      </select>
                      <input type="text" value={param.description} onChange={e => setEditingApiTool(p => ({ ...p, params: p.params.map((x, i) => i === idx ? { ...x, description: e.target.value } : x) }))} placeholder="description for AI" className="col-span-4 px-2 py-1.5 bg-white border border-[#E5E5E5] rounded text-[13px]" />
                      <label className="col-span-1 flex items-center gap-1 text-[12px] text-[#706E6B]">
                        <input type="checkbox" checked={param.required} onChange={e => setEditingApiTool(p => ({ ...p, params: p.params.map((x, i) => i === idx ? { ...x, required: e.target.checked } : x) }))} /> req
                      </label>
                      <button type="button" onClick={() => setEditingApiTool(p => ({ ...p, params: p.params.filter((_, i) => i !== idx) }))} className="col-span-1 p-1 text-[#BA0517] hover:bg-[#FDE7E9] rounded flex justify-center"><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>

                {/* Response field mapping */}
                <div className="pt-1 border-t border-[#D8DDE6] space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-bold text-[#706E6B] uppercase ">Response field mapping <span className="normal-case font-normal">(blank = pass full response)</span></p>
                    <button type="button" onClick={() => setEditingApiTool(p => ({ ...p, responseFields: [...p.responseFields, { label: '', path: '' }] }))} className="flex items-center gap-1 px-2 py-1 bg-[#EAF4FE] text-[#0176D3] rounded text-[13px] font-semibold">
                      <Plus size={11} /> Add field
                    </button>
                  </div>
                  {editingApiTool.responseFields.map((rf, idx) => (
                    <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-1.5 items-center">
                      <input type="text" value={rf.label} onChange={e => setEditingApiTool(p => ({ ...p, responseFields: p.responseFields.map((x, i) => i === idx ? { ...x, label: e.target.value } : x) }))} placeholder="label (key in result)" className="col-span-5 px-2 py-1.5 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono" />
                      <input type="text" value={rf.path} onChange={e => setEditingApiTool(p => ({ ...p, responseFields: p.responseFields.map((x, i) => i === idx ? { ...x, path: e.target.value } : x) }))} placeholder="dot-path e.g. data.status" className="col-span-6 px-2 py-1.5 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono" />
                      <button type="button" onClick={() => setEditingApiTool(p => ({ ...p, responseFields: p.responseFields.filter((_, i) => i !== idx) }))} className="col-span-1 p-1 text-[#BA0517] hover:bg-[#FDE7E9] rounded flex justify-center"><Trash2 size={12} /></button>
                    </div>
                  ))}
                </div>

                {/* Test section (only available on saved tools) */}
                {editingApiTool._id && (
                  <div className="pt-1 border-t border-[#D8DDE6] space-y-2">
                    <p className="text-[11px] font-bold text-[#706E6B] uppercase ">Test this tool</p>
                    {editingApiTool.params.filter(p => p.required || p.name).map(param => (
                      <div key={param.name} className="flex gap-2 items-center">
                        <label className="text-[12px] text-[#706E6B] font-mono w-28 shrink-0">{param.name}</label>
                        <input type="text" value={apiToolTestArgs[param.name] || ''} onChange={e => setApiToolTestArgs(prev => ({ ...prev, [param.name]: e.target.value }))} placeholder={`value for ${param.name}`} className="flex-1 px-2 py-1.5 bg-white border border-[#E5E5E5] rounded text-[13px]" />
                      </div>
                    ))}
                    <button type="button" onClick={() => testApiTool(editingApiTool)} disabled={testingApiToolId === editingApiTool._id} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#2E844A] text-white rounded text-[13px] font-semibold disabled:opacity-60">
                      {testingApiToolId === editingApiTool._id ? <RefreshCw size={12} className="animate-spin" /> : <Play size={12} />} Run test call
                    </button>
                    {apiToolTestResult && (
                      <div className="bg-[#080707] rounded-lg p-3">
                        {apiToolTestResult.error ? (
                          <p className="text-[11px] text-[#BA0517] font-mono">{apiToolTestResult.error}</p>
                        ) : (
                          <>
                            <p className="text-[10px] text-[#706E6B] mb-1">HTTP {apiToolTestResult.status} · {apiToolTestResult.urlCalled}</p>
                            {apiToolTestResult.mapped && Object.keys(apiToolTestResult.mapped).length > 0 && (
                              <div className="mb-2">
                                <p className="text-[10px] text-[#2E844A] font-bold mb-1">Mapped fields:</p>
                                <pre className="text-[10px] text-[#2E844A] font-mono whitespace-pre-wrap">{JSON.stringify(apiToolTestResult.mapped, null, 2)}</pre>
                              </div>
                            )}
                            <p className="text-[10px] text-[#706E6B] font-bold mb-1">Raw response:</p>
                            <pre className="text-[10px] text-[#C9C9C9] font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">{JSON.stringify(apiToolTestResult.raw, null, 2)}</pre>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <label className="flex items-center gap-2 text-sm font-medium text-[#080707]">
                  <input type="checkbox" checked={editingApiTool.isActive} onChange={e => setEditingApiTool(p => ({ ...p, isActive: e.target.checked }))} />
                  Active (agent can call this tool)
                </label>

                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => { setEditingApiTool(null); setApiToolTestResult(null); }} className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded hover:bg-[#F3F2F2]">Cancel</button>
                  <button type="button" onClick={saveApiTool} disabled={savingApiTool} className="hover:bg-[#014486] flex items-center gap-1.5 px-4 py-2 bg-[#0176D3] text-white rounded whitespace-nowrap text-[13px] font-semibold disabled:opacity-60">
                    {savingApiTool ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save Tool
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => { setEditingApiTool({ ...BLANK_API_TOOL }); setApiToolTestResult(null); setApiToolTestArgs({}); }} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EAF4FE] text-[#0176D3] rounded text-[13px] font-semibold">
                <Plus size={14} /> Add API Tool
              </button>
            )}
          </div>

          <div className="sf-card p-6 space-y-4">
            <h2 className="sf-card-title flex items-center gap-1.5"><Server size={16} className="text-[#0176D3]" /> MCP Tools</h2>
            <p className="text-xs text-[#706E6B]">
              Connect the agent to an external MCP (Model Context Protocol) server as a live client — its
              tools get discovered automatically, and whichever ones you enable become callable by the
              agent in real time, the same way built-in and API Playground tools are.
            </p>

            {mcpConnectors.length === 0 && !editingMcpConnector && (
              <p className="text-xs text-[#706E6B] italic">No MCP connector configured yet.</p>
            )}

            <div className="space-y-2">
              {mcpConnectors.map(c => (
                <div key={c._id} className="border border-[#D8DDE6] rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-[#080707]">{c.name}</p>
                      <p className="text-xs text-[#706E6B] font-mono">{c.serverUrl}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`sf-badge ${c.discoveryStatus === 'ready' ? 'sf-badge-success' : c.discoveryStatus === 'failed' ? 'sf-badge-error' : 'sf-badge-warning'}`}>
                        {c.discoveryStatus}{c.discoveryStatus === 'ready' ? ` · ${c.tools.length} tools` : ''}
                      </span>
                      <button
                        type="button"
                        onClick={() => discoverMcpTools(c._id)}
                        disabled={discoveringMcpId === c._id || c.discoveryStatus === 'discovering'}
                        className="flex items-center gap-1 px-2 py-1 bg-[#EAF4FE] text-[#0176D3] rounded text-[13px] font-semibold disabled:opacity-60"
                      >
                        <RefreshCw size={12} className={(discoveringMcpId === c._id || c.discoveryStatus === 'discovering') ? 'animate-spin' : ''} /> Discover
                      </button>
                      <button type="button" onClick={() => setEditingMcpConnector({ ...BLANK_MCP_CONNECTOR, ...c, authValue: '' })} className="px-2 py-1 text-[13px] font-semibold text-[#0176D3] hover:bg-[#EAF4FE] rounded">Edit</button>
                      <button type="button" onClick={() => deleteMcpConnector(c._id)} className="p-1.5 text-[#BA0517] hover:bg-[#FDE7E9] rounded"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  {c.discoveryStatus === 'failed' && c.discoveryError && (
                    <p className="text-[11px] text-[#BA0517]">{c.discoveryError}</p>
                  )}
                  {c.tools?.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1 border-t border-[#D8DDE6]">
                      {c.tools.map(t => (
                        <label key={t.name} className="flex items-center gap-2 text-[12px] text-[#080707]" title={t.description}>
                          <input type="checkbox" checked={t.enabled} onChange={() => toggleMcpConnectorTool(c, t.name)} />
                          <span className="font-mono truncate">{t.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {editingMcpConnector ? (
              <div className="border border-[#D8DDE6] rounded-lg p-4 space-y-3 bg-[#F9FAFB]">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Name</label>
                    <input type="text" value={editingMcpConnector.name} onChange={e => setEditingMcpConnector(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Internal Tools Server" className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px]" />
                  </div>
                  <div>
                    <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Server URL</label>
                    <input type="text" value={editingMcpConnector.serverUrl} onChange={e => setEditingMcpConnector(p => ({ ...p, serverUrl: e.target.value }))} placeholder="https://mcp.example.com" className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Auth Type</label>
                    <select value={editingMcpConnector.authType} onChange={e => setEditingMcpConnector(p => ({ ...p, authType: e.target.value }))} className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px]">
                      <option value="none">None</option>
                      <option value="bearer">Bearer token</option>
                      <option value="header">Custom header</option>
                    </select>
                  </div>
                  {editingMcpConnector.authType === 'header' && (
                    <div>
                      <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">Header Name</label>
                      <input type="text" value={editingMcpConnector.authKeyName} onChange={e => setEditingMcpConnector(p => ({ ...p, authKeyName: e.target.value }))} placeholder="X-API-Key" className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono" />
                    </div>
                  )}
                  {editingMcpConnector.authType !== 'none' && (
                    <div className={editingMcpConnector.authType === 'bearer' ? 'col-span-2' : ''}>
                      <label className="block text-[12px] font-semibold text-[#706E6B] mb-1">
                        {editingMcpConnector._id ? 'New Secret (leave blank to keep existing)' : 'Secret'}
                      </label>
                      <input type="password" value={editingMcpConnector.authValue} onChange={e => setEditingMcpConnector(p => ({ ...p, authValue: e.target.value }))} className="w-full px-3 py-2 bg-white border border-[#E5E5E5] rounded text-[13px] font-mono" />
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setEditingMcpConnector(null)} className="h-9 px-4 bg-white border border-[#C9C9C9] text-[#0176D3] text-[13px] font-semibold rounded hover:bg-[#F3F2F2]">Cancel</button>
                  <button type="button" onClick={saveMcpConnector} disabled={savingMcpConnector} className="hover:bg-[#014486] flex items-center gap-1.5 px-4 py-2 bg-[#0176D3] text-white rounded whitespace-nowrap text-[13px] font-semibold disabled:opacity-60">
                    {savingMcpConnector ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save Connector
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" onClick={() => setEditingMcpConnector({ ...BLANK_MCP_CONNECTOR })} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EAF4FE] text-[#0176D3] rounded text-[13px] font-semibold">
                <Plus size={14} /> Add MCP Connector
              </button>
            )}
          </div>

          <div className="sf-card p-6 space-y-3">
            <h2 className="sf-card-title">Tools</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {ALL_TOOLS.map(tool => (
                <label key={tool.key} className="flex items-center gap-2 text-sm font-medium text-[#080707]">
                  <input type="checkbox" checked={widget.enabledTools.includes(tool.key)} onChange={() => toggleTool(tool.key)} />
                  {tool.label}
                </label>
              ))}
            </div>
            {apiTools.filter(t => t.isActive).length > 0 && (
              <div className="pt-3 border-t border-[#D8DDE6]">
                <p className="text-[11px] font-bold text-[#706E6B] uppercase mb-2">REST API Tools</p>
                <label className="flex items-center gap-2 text-sm text-[#080707] mb-2">
                  <input
                    type="checkbox"
                    checked={!Array.isArray(widget.apiToolIds)}
                    onChange={e => patch(['apiToolIds'], e.target.checked ? null : apiTools.filter(t => t.isActive).map(t => t._id))}
                  />
                  This agent can use all API tools
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {apiTools.filter(t => t.isActive).map(t => {
                    const scoped = Array.isArray(widget.apiToolIds);
                    return (
                      <label key={t._id} className={`flex items-center gap-2 text-sm text-[#080707] ${scoped ? '' : 'opacity-60'}`}>
                        <input
                          type="checkbox"
                          disabled={!scoped}
                          checked={scoped ? widget.apiToolIds.includes(t._id) : true}
                          onChange={() => patch(['apiToolIds'], widget.apiToolIds.includes(t._id) ? widget.apiToolIds.filter(id => id !== t._id) : [...widget.apiToolIds, t._id])}
                        />
                        <Zap size={13} className="text-[#0176D3] shrink-0" />
                        <span className="font-mono text-xs truncate" title={t.description}>{t.name}</span>
                        <span className="text-[10px] uppercase text-[#706E6B]">{t.riskTier || (t.method === 'GET' ? 'read' : 'write')}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="sf-card p-6 space-y-4">
            <h2 className="sf-card-title">What the agent may do on its own</h2>
            <p className="text-xs text-[#706E6B]">Every tool has a risk level: read (look things up), write (create/update records, send messages), money (payment links, charges) and destructive (cancel/delete). Anything above the level you choose waits in <b>Agent Approvals</b> for a team member to approve.</p>
            <div className="max-w-sm">
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Run without approval up to</label>
              <select value={widget.approvals?.autoApproveUpTo || 'write'} onChange={e => patch(['approvals', 'autoApproveUpTo'], e.target.value)} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                <option value="read">Read only (approve everything else)</option>
                <option value="write">Read and write (approve money and destructive) — recommended</option>
                <option value="money">Everything except destructive</option>
                <option value="destructive">Everything (no approvals)</option>
              </select>
            </div>
          </div>

          <div className="sf-card p-6 space-y-4">
            <h2 className="sf-card-title">AI model</h2>
            <p className="text-xs text-[#706E6B]">Choose which provider answers for this agent. If the chosen provider has no key (or is down), the other one is used when you have a key for it. A model name is only used for the provider it belongs to.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Provider</label>
                <select value={widget.model?.provider || ''} onChange={e => patch(['model', 'provider'], e.target.value || undefined)} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                  <option value="">Platform default</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Claude (Anthropic)</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">If it fails, use</label>
                <select value={widget.model?.fallbackProvider || ''} onChange={e => patch(['model', 'fallbackProvider'], e.target.value || undefined)} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]">
                  <option value="">The other provider (if keyed)</option>
                  <option value="none">No fallback</option>
                </select>
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Model name (optional)</label>
                <input type="text" value={widget.model?.name || ''} onChange={e => patch(['model', 'name'], e.target.value)} placeholder="Provider default" className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
              </div>
            </div>
          </div>

          <div className="sf-card p-6 space-y-4">
            <h2 className="sf-card-title">Token usage limits</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Monthly token budget</label>
                <input type="number" min="0" value={widget.tokenLimits.monthlyBudget} onChange={e => patch(['tokenLimits', 'monthlyBudget'], Number(e.target.value))} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
              </div>
              <div>
                <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Per-conversation limit</label>
                <input type="number" min="0" value={widget.tokenLimits.perConversationLimit} onChange={e => patch(['tokenLimits', 'perConversationLimit'], Number(e.target.value))} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" />
              </div>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#706E6B] mb-1.5">Message shown once the budget is hit</label>
              <textarea value={widget.tokenLimits.overBudgetMessage} onChange={e => patch(['tokenLimits', 'overBudgetMessage'], e.target.value)} className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] h-16" />
            </div>
          </div>

          <div className="sf-card p-6 space-y-3">
            <h2 className="sf-card-title">Allowed domains</h2>
            <p className="text-xs text-[#706E6B]">Leave empty to allow any domain. Otherwise, only requests whose Origin/Referer contains one of these will be served.</p>
            <div className="flex gap-2">
              <input type="text" value={domainInput} onChange={e => setDomainInput(e.target.value)} placeholder="example.com" className="flex-1 px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px]" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDomain())} />
              <button type="button" onClick={addDomain} className="hover:bg-[#014486] px-4 py-2 bg-[#0176D3] text-white rounded whitespace-nowrap text-[13px] font-semibold">Add</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {(widget.allowedDomains || []).map(d => (
                <span key={d} className="sf-badge sf-badge-neutral flex items-center gap-1.5">
                  {d} <button type="button" onClick={() => removeDomain(d)} className="text-[#706E6B] hover:text-[#BA0517]">&times;</button>
                </span>
              ))}
            </div>
          </div>

          <div className="sf-card p-6 space-y-3">
            <h2 className="sf-card-title flex items-center gap-1.5"><Webhook size={16} className="text-[#0176D3]" /> Webhooks</h2>
            <p className="text-xs text-[#706E6B]">
              Notify an external system (Slack, Zapier, your own backend) when the agent hits an event.
              The agent never calls arbitrary APIs itself — this only pushes notifications out to URLs
              you register here. Only public http(s) URLs are accepted; localhost and private/internal
              addresses are rejected.
            </p>
            <div className="space-y-3">
              {(widget.webhooks || []).map((wh, idx) => (
                <div key={idx} className="border border-[#D8DDE6] rounded-lg p-3 space-y-2">
                  <div className="flex gap-2 items-start">
                    <input
                      type="text"
                      value={wh.url}
                      onChange={e => patchWebhook(idx, 'url', e.target.value)}
                      placeholder="https://hooks.slack.com/services/…"
                      className="flex-1 px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono"
                    />
                    <button type="button" onClick={() => removeWebhook(idx)} className="p-2 text-[#BA0517] hover:bg-[#FDE7E9] rounded"><Trash2 size={16} /></button>
                  </div>
                  <input
                    type="text"
                    value={wh.secret || ''}
                    onChange={e => patchWebhook(idx, 'secret', e.target.value)}
                    placeholder="Signing secret (optional — verifies X-Webhook-Signature)"
                    className="w-full px-3 py-2 bg-[#F7F9FB] border border-[#D8DDE6] rounded text-[13px] font-mono"
                  />
                  <div className="flex flex-wrap gap-3">
                    {WEBHOOK_EVENTS.map(ev => (
                      <label key={ev.key} className="flex items-center gap-1.5 text-[12px] font-medium text-[#080707]">
                        <input type="checkbox" checked={wh.events.includes(ev.key)} onChange={() => toggleWebhookEvent(idx, ev.key)} />
                        {ev.label}
                      </label>
                    ))}
                    <label className="flex items-center gap-1.5 text-[12px] font-medium text-[#080707] ml-auto">
                      <input type="checkbox" checked={wh.isActive} onChange={e => patchWebhook(idx, 'isActive', e.target.checked)} />
                      Active
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={addWebhook} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#EAF4FE] text-[#0176D3] rounded whitespace-nowrap text-[13px] font-semibold">
              <Plus size={14} /> Add Webhook
            </button>
          </div>

          <div className="sf-card p-6 space-y-3">
            <h2 className="sf-card-title flex items-center gap-1.5"><Globe size={16} className="text-[#0176D3]" /> Channels</h2>
            <label className="flex items-start gap-2 text-sm font-medium text-[#080707]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!widget.channels?.whatsappBotEnabled}
                onChange={e => patch(['channels', 'whatsappBotEnabled'], e.target.checked)}
              />
              <span>
                Enable WhatsApp Bot
                <span className="block text-xs font-normal text-[#706E6B] mt-0.5">
                  When enabled, incoming Meta WhatsApp messages for your account are routed through this agent —
                  the same tools, knowledge bases, model, and system prompt are used on both the website widget
                  and WhatsApp. Configure your WhatsApp number in Settings → Meta WhatsApp.
                </span>
              </span>
            </label>
            {!!widget.channels?.whatsappBotEnabled && (
              <div className="ml-6 mt-2">
                <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Reply delay (ms)</label>
                <input
                  type="number"
                  min={0}
                  step={500}
                  value={widget.channels?.whatsappBotReplyDelay ?? 0}
                  onChange={e => patch(['channels', 'whatsappBotReplyDelay'], Number(e.target.value))}
                  className="w-32 px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px]"
                />
                <p className="text-xs text-[#706E6B] mt-1">Milliseconds to wait before replying (0 = instant). Adds a natural typing delay.</p>

                <label className="flex items-start gap-2 text-sm font-medium text-[#080707] pt-3 mt-3 border-t border-[#E5E5E5]">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!widget.channels?.whatsappIdentityVerification?.enabled}
                    onChange={e => patch(['channels', 'whatsappIdentityVerification', 'enabled'], e.target.checked)}
                  />
                  <span>
                    Require Identity Verification
                    <span className="block text-xs font-normal text-[#706E6B] mt-0.5">
                      Before any tool marked "Requires Verified Identity" (in API Playground) can run for a WhatsApp
                      caller, they must first verify — required for sensitive data like account balances.
                    </span>
                  </span>
                </label>

                {!!widget.channels?.whatsappIdentityVerification?.enabled && (
                  <div className="ml-6 mt-2 space-y-3 max-w-md">
                    <div>
                      <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Verification Method</label>
                      <select
                        value={widget.channels?.whatsappIdentityVerification?.method || 'otp'}
                        onChange={e => patch(['channels', 'whatsappIdentityVerification', 'method'], e.target.value)}
                        className="w-full px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px]"
                      >
                        <option value="otp">OTP only (verifies the WhatsApp number itself)</option>
                        <option value="external_lookup">External lookup only (confirms phone matches a real customer)</option>
                        <option value="external_lookup_plus_otp">External lookup + OTP (both)</option>
                      </select>
                    </div>

                    {(widget.channels?.whatsappIdentityVerification?.method === 'external_lookup' || widget.channels?.whatsappIdentityVerification?.method === 'external_lookup_plus_otp') && (
                      <>
                        <div>
                          <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Lookup API Tool</label>
                          <select
                            value={widget.channels?.whatsappIdentityVerification?.lookupApiToolId || ''}
                            onChange={e => patch(['channels', 'whatsappIdentityVerification', 'lookupApiToolId'], e.target.value)}
                            className="w-full px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px]"
                          >
                            <option value="">Select a tool…</option>
                            {apiTools.filter(t => t.resultMode !== 'list').map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                          </select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Phone param name</label>
                            <input type="text" value={widget.channels?.whatsappIdentityVerification?.lookupPhoneParamName || ''}
                              onChange={e => patch(['channels', 'whatsappIdentityVerification', 'lookupPhoneParamName'], e.target.value)}
                              placeholder="phone" className="w-full px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px] font-mono" />
                          </div>
                          <div>
                            <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Identifier response field</label>
                            <input type="text" value={widget.channels?.whatsappIdentityVerification?.identifierResponseField || ''}
                              onChange={e => patch(['channels', 'whatsappIdentityVerification', 'identifierResponseField'], e.target.value)}
                              placeholder="customerId" className="w-full px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px] font-mono" />
                          </div>
                        </div>
                      </>
                    )}

                    {(widget.channels?.whatsappIdentityVerification?.method === 'otp' || widget.channels?.whatsappIdentityVerification?.method === 'external_lookup_plus_otp') && (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">OTP Template Name</label>
                            <input type="text" value={widget.channels?.whatsappIdentityVerification?.otpTemplateName || ''}
                              onChange={e => patch(['channels', 'whatsappIdentityVerification', 'otpTemplateName'], e.target.value)}
                              placeholder="account_verification" className="w-full px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px] font-mono" />
                          </div>
                          <div>
                            <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Template Language</label>
                            <input type="text" value={widget.channels?.whatsappIdentityVerification?.otpTemplateLanguage || 'en_US'}
                              onChange={e => patch(['channels', 'whatsappIdentityVerification', 'otpTemplateLanguage'], e.target.value)}
                              className="w-full px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px] font-mono" />
                          </div>
                        </div>
                        <p className="text-xs text-[#706E6B]">Must be an approved Meta WhatsApp template with an OTP button component.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Code expiry (minutes)</label>
                            <input type="number" min={1} value={widget.channels?.whatsappIdentityVerification?.otpExpiryMinutes ?? 10}
                              onChange={e => patch(['channels', 'whatsappIdentityVerification', 'otpExpiryMinutes'], Number(e.target.value))}
                              className="w-full px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px]" />
                          </div>
                          <div>
                            <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Max attempts</label>
                            <input type="number" min={1} value={widget.channels?.whatsappIdentityVerification?.maxOtpAttempts ?? 5}
                              onChange={e => patch(['channels', 'whatsappIdentityVerification', 'maxOtpAttempts'], Number(e.target.value))}
                              className="w-full px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px]" />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            <label className="flex items-start gap-2 text-sm font-medium text-[#080707] pt-3 border-t border-[#E5E5E5]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!widget.channels?.instagramBotEnabled}
                onChange={e => patch(['channels', 'instagramBotEnabled'], e.target.checked)}
              />
              <span>
                Enable Instagram Bot
                <span className="block text-xs font-normal text-[#706E6B] mt-0.5">
                  When enabled, incoming Instagram Direct Messages for your account are routed through this
                  agent — same tools, knowledge bases, model, and system prompt as the website widget and
                  WhatsApp. Explicit keyword automation rules (Instagram → Automation Rules) still take
                  priority over the AI agent. Configure your Instagram connection in Instagram → Connect & Settings.
                </span>
              </span>
            </label>
            {!!widget.channels?.instagramBotEnabled && (
              <div className="ml-6 mt-2">
                <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Reply delay (ms)</label>
                <input
                  type="number"
                  min={0}
                  step={500}
                  value={widget.channels?.instagramBotReplyDelay ?? 0}
                  onChange={e => patch(['channels', 'instagramBotReplyDelay'], Number(e.target.value))}
                  className="w-32 px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px]"
                />
                <p className="text-xs text-[#706E6B] mt-1">Milliseconds to wait before replying (0 = instant). Adds a natural typing delay.</p>
              </div>
            )}

            <label className="flex items-start gap-2 text-sm font-medium text-[#080707] pt-3 border-t border-[#E5E5E5]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!widget.channels?.facebookBotEnabled}
                onChange={e => patch(['channels', 'facebookBotEnabled'], e.target.checked)}
              />
              <span>
                Enable Facebook Bot
                <span className="block text-xs font-normal text-[#706E6B] mt-0.5">
                  When enabled, incoming Facebook Page Messenger DMs for your account are routed through this
                  agent — same tools, knowledge bases, model, and system prompt as every other channel.
                  Explicit keyword automation rules (Facebook → Automation Rules) still take priority over
                  the AI agent. Configure your Facebook connection in Facebook → Connect & Settings.
                </span>
              </span>
            </label>
            {!!widget.channels?.facebookBotEnabled && (
              <div className="ml-6 mt-2">
                <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Reply delay (ms)</label>
                <input
                  type="number"
                  min={0}
                  step={500}
                  value={widget.channels?.facebookBotReplyDelay ?? 0}
                  onChange={e => patch(['channels', 'facebookBotReplyDelay'], Number(e.target.value))}
                  className="w-32 px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px]"
                />
                <p className="text-xs text-[#706E6B] mt-1">Milliseconds to wait before replying (0 = instant). Adds a natural typing delay.</p>
              </div>
            )}

            <label className="flex items-start gap-2 text-sm font-medium text-[#080707] pt-3 border-t border-[#E5E5E5]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!widget.channels?.telegramBotEnabled}
                onChange={e => patch(['channels', 'telegramBotEnabled'], e.target.checked)}
              />
              <span>
                Enable Telegram Bot
                <span className="block text-xs font-normal text-[#706E6B] mt-0.5">
                  When enabled, text messages sent to your connected Telegram bot are answered by this agent —
                  same tools, knowledge bases, model, and system prompt as every other channel. Staff can still take over any chat.
                </span>
              </span>
            </label>
            {!!widget.channels?.telegramBotEnabled && (
              <div className="ml-6 mt-2">
                <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Reply delay (ms)</label>
                <input
                  type="number"
                  min={0}
                  step={500}
                  value={widget.channels?.telegramBotReplyDelay ?? 0}
                  onChange={e => patch(['channels', 'telegramBotReplyDelay'], Number(e.target.value))}
                  className="w-32 px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px]"
                />
                <p className="text-xs text-[#706E6B] mt-1">Milliseconds to wait before replying (0 = instant).</p>
              </div>
            )}

            <label className="flex items-start gap-2 text-sm font-medium text-[#080707] pt-3 border-t border-[#E5E5E5]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!widget.channels?.identityVerification?.enabled}
                onChange={e => patch(['channels', 'identityVerification', 'enabled'], e.target.checked)}
              />
              <span>
                Verify customers by email code (web widget, Instagram, Messenger, email, Telegram, API)
                <span className="block text-xs font-normal text-[#706E6B] mt-0.5">
                  When a tool needs to know who the customer is (invoice or order status), the agent asks for the email on their
                  account and confirms a 6-digit code sent to it. Only emails already in your CRM get a code. Verification lasts 24 hours.
                  WhatsApp has its own verification setting.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm font-medium text-[#080707] pt-3 border-t border-[#E5E5E5]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!widget.channels?.apiBotEnabled}
                onChange={e => patch(['channels', 'apiBotEnabled'], e.target.checked)}
              />
              <span>
                Enable Agent API
                <span className="block text-xs font-normal text-[#706E6B] mt-0.5">
                  Lets your own chatbot or app talk to this agent over HTTP (POST /api/v1/agent/conversations/&#123;id&#125;/messages)
                  with an API key that has the "agent" scope. Same tools, approvals and human takeover as every other channel.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm font-medium text-[#080707] pt-3 border-t border-[#E5E5E5]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={!!widget.channels?.emailBotEnabled}
                onChange={e => patch(['channels', 'emailBotEnabled'], e.target.checked)}
              />
              <span>
                Enable Email Bot
                <span className="block text-xs font-normal text-[#706E6B] mt-0.5">
                  When enabled, emails forwarded to your inbound support address below are routed through
                  this agent — same tools, knowledge bases, model, and system prompt as every other channel.
                </span>
              </span>
            </label>
            {!!widget.channels?.emailBotEnabled && (
              <div className="ml-6 mt-2 space-y-3">
                <div>
                  <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Reply delay (ms)</label>
                  <input
                    type="number"
                    min={0}
                    step={500}
                    value={widget.channels?.emailBotReplyDelay ?? 0}
                    onChange={e => patch(['channels', 'emailBotReplyDelay'], Number(e.target.value))}
                    className="w-32 px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px]"
                  />
                  <p className="text-xs text-[#706E6B] mt-1">Milliseconds to wait before replying (0 = instant).</p>
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Your inbound support address</label>
                  {inboundEmail ? (
                    <div className="flex items-center gap-2">
                      <code className="px-2 py-1.5 bg-[#F3F2F2] border border-[#D8DDE6] rounded text-sm">{inboundEmail}</code>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(inboundEmail); alertService.success('Copied'); }}
                        className="p-1.5 text-[#706E6B] hover:text-[#080707]"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-[#706E6B]">Save the widget once to provision your inbound address.</p>
                  )}
                  <p className="text-xs text-[#706E6B] mt-1">
                    Forward or auto-redirect your real support inbox (e.g. support@yourdomain.com) to this address
                    so incoming emails reach the AI agent.
                  </p>
                </div>
              </div>
            )}

            <div className="pt-3 border-t border-[#E5E5E5]">
              <p className="text-sm font-semibold text-[#080707] mb-1">Razorpay Payment Keys</p>
              <p className="text-xs text-[#706E6B] mb-3">
                Required if you enable the <strong>Generate Payment Link</strong> tool. Add your own Razorpay API keys so the bot can collect payments on your behalf.
              </p>
              <div className="space-y-2">
                <div>
                  <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Key ID</label>
                  <input
                    type="text"
                    placeholder="rzp_live_xxxxxxxxxxxx"
                    value={widget.channels?.razorpayKeyId ?? ''}
                    onChange={e => patch(['channels', 'razorpayKeyId'], e.target.value)}
                    className="w-full px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px] font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-medium text-[#3E3E3C] mb-1">Key Secret</label>
                  <input
                    type="password"
                    placeholder="••••••••••••••••"
                    value={widget.channels?.razorpayKeySecret ?? ''}
                    onChange={e => patch(['channels', 'razorpayKeySecret'], e.target.value)}
                    className="w-full px-3 py-1.5 border border-[#D8DDE6] rounded text-[13px] font-mono"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="sf-card p-6 space-y-4">
            <h2 className="sf-card-title flex items-center gap-1.5"><ShieldCheck size={16} className="text-[#2E844A]" /> Security</h2>
            <label className="flex items-start gap-2 text-sm font-medium text-[#080707]">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={widget.security?.blockPromptInjection !== false}
                onChange={e => patch(['security', 'blockPromptInjection'], e.target.checked)}
              />
              <span>
                Block prompt-injection &amp; jailbreak attempts
                <span className="block text-xs font-normal text-[#706E6B] mt-0.5">
                  Screens visitor messages for common jailbreak patterns before they reach the model, and
                  screens the model's replies for signs of instruction leakage — flagged messages get a
                  safe deflection instead of a real model response.
                </span>
              </span>
            </label>
          </div>

          <div className="flex justify-end">
            <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#0176D3] hover:bg-[#014486] disabled:opacity-60 text-white px-4 h-9 rounded whitespace-nowrap font-semibold">
              {saving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />} Save Widget
            </button>
          </div>
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="sf-card p-6 space-y-4">
            <h2 className="sf-card-title flex items-center gap-1.5"><Sparkles size={16} className="text-[#DD7A01]" /> Live Preview</h2>
            <div className="border border-[#D8DDE6] rounded-lg overflow-hidden" style={{ height: 520 }}>
              <iframe key={widget.publicWidgetKey} src={previewUrl} title="Widget preview" style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>

          <div className="bg-[#080707] text-white rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-sm">Embed Code</h2>
              <button onClick={copyEmbedCode} className="p-1.5 bg-[#0176D3] hover:bg-[#014486] rounded text-[#706E6B] hover:text-white transition">
                {copied ? <Check size={16} className="text-[#2E844A]" /> : <Copy size={16} />}
              </button>
            </div>
            <pre className="bg-[#080707] p-4 rounded-lg text-[#C9C9C9] font-mono text-[10px] whitespace-pre-wrap break-all leading-normal">{embedCode}</pre>
            <p className="text-[11px] text-[#706E6B] leading-normal">
              Paste this one line before the closing &lt;/body&gt; tag on any website — WordPress, a static
              site, or any framework. It works independently of the host site's technology.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
